import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { formatAdsTime } from 'utils/date';
import { calcSizeForTv } from 'utils/index';
import { modifyVastUrlCTV } from 'utils/vast';

import { usePlayer } from '../context';

import { Icons } from 'components/Icons';
import { WebDiv } from 'components/WebDiv';

type AdsType = {
  adsRemainingTime: string;
  adPosition: number;
  adDuration: number;
  totalAds: number;
};

const BACK_EVENT = 'back-remote-control';

const MESSAGES = {
  WARN_CONTAINER_NOT_FOUND: '[CTV Ads][WARN] Video or Ad container not found yet.',
  WARN_IMA_SDK_NOT_AVAILABLE: '[CTV Ads][WARN] IMA SDK not available on window.google',
  ERROR_INITIALIZE_IMA_SDK: '[CTV Ads][ERROR] Failed to initialize IMA SDK:',
  ERROR_AD_PLAYBACK: '[CTV Ads][ERROR] Ad playback error:',
  ERROR_IMA_LOADING: '[CTV Ads][ERROR] Error while loading IMA SDK:',
  ERROR_CLEANING: '[CTV Ads][ERROR] Error cleaning up IMA SDK',
  ERROR_UNKNOWN: '[CTV Ads][ERROR] Unknown ad-related error:',
} as const;

const CTVPlayerWithAds: React.FC = () => {
  const { isVisible, adTagUrl, pause, setIsVisible, setFullScreen, setShowingAdsCTV } = usePlayer();

  const [isAdsPlaying, setIsAdsPlaying] = useState<boolean>(false);
  const [adsInfo, setAdsInfo] = useState<AdsType>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const adContainerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const adsLoaderRef = useRef<any>(null);
  const adsManagerRef = useRef<any>(null);
  const adDisplayContainerRef = useRef<any>(null);
  const { width, height } = useWindowDimensions();

  const TogglePlayPauseIcon = isAdsPlaying ? Icons.SolidPause : Icons.Play;

  useEffect(() => {
    let isMounted = true;

    if (!adTagUrl || !isVisible) return;

    const loadIma = async () => {
      if (!isMounted) return;

      if (!window.google?.ima) {
        await new Promise<void>((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
          script.onload = () => resolve();
          document.body.appendChild(script);
        });
      }

      const container = adContainerRef.current;
      const videoElement = videoRef.current;

      if (!container || !videoElement) {
        console.warn(MESSAGES.WARN_CONTAINER_NOT_FOUND);
        return;
      }

      try {
        initIMA(container, videoElement);
      } catch (err) {
        console.error(MESSAGES.ERROR_INITIALIZE_IMA_SDK, err);
      }
    };

    const initIMA = (container: HTMLElement, videoElement: HTMLVideoElement) => {
      if (!window.google?.ima) {
        console.warn(MESSAGES.WARN_IMA_SDK_NOT_AVAILABLE);
        return;
      }

      adDisplayContainerRef.current = new window.google.ima.AdDisplayContainer(
        container,
        videoElement
      );

      adsLoaderRef.current = new window.google.ima.AdsLoader(adDisplayContainerRef.current);

      adsLoaderRef.current.addEventListener(
        window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        onAdsManagerLoaded
      );

      adsLoaderRef.current.addEventListener(
        window.google.ima.AdErrorEvent.Type.AD_ERROR,
        onAdError
      );

      const adsRequest = new window.google.ima.AdsRequest();
      adsRequest.adTagUrl = modifyVastUrlCTV(adTagUrl);
      adsRequest.contentUrl = window.location.href;
      adsRequest.linearAdSlotWidth = width;
      adsRequest.linearAdSlotHeight = height;

      adDisplayContainerRef.current.initialize();
      adsLoaderRef.current.requestAds(adsRequest);
    };

    const onAdError = (error: any) => {
      console.error(MESSAGES.ERROR_AD_PLAYBACK, error);
      setShowingAdsCTV(false);
      setIsLoading(false);
    };

    const onAdProgress = (progressEvent: any) => {
      const data = progressEvent?.getAdData?.();
      if (!data) return;
      const remaining = data.duration - data.currentTime;

      setAdsInfo((prev) => {
        const newAdsInfo = {
          adsRemainingTime: formatAdsTime(remaining),
          adPosition: data.adPosition,
          adDuration: data.duration,
          totalAds: data.totalAds,
        };

        if (
          prev?.adsRemainingTime === newAdsInfo.adsRemainingTime &&
          prev?.adPosition === newAdsInfo.adPosition &&
          prev?.adDuration === newAdsInfo.adDuration
        )
          return prev;

        return newAdsInfo;
      });
    };

    const onAdCompleted = () => {
      setIsLoading(true);
    };

    const onAdsManagerLoaded = (event: any) => {
      if (!window.google?.ima) {
        console.warn(MESSAGES.WARN_IMA_SDK_NOT_AVAILABLE);
        return;
      }

      const adsRenderingSettings = new window.google.ima.AdsRenderingSettings();
      adsManagerRef.current = event.getAdsManager(videoRef.current, adsRenderingSettings);

      const manager = adsManagerRef.current;

      const imaAdEvents = window.google.ima.AdEvent.Type;

      manager.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, onAdError);
      manager.addEventListener(imaAdEvents.AD_CAN_PLAY, () => setIsLoading(false));
      manager.addEventListener(imaAdEvents.STARTED, () => setIsAdsPlaying(true));
      manager.addEventListener(imaAdEvents.PAUSED, () => setIsAdsPlaying(false));
      manager.addEventListener(imaAdEvents.RESUMED, () => setIsAdsPlaying(true));
      manager.addEventListener(imaAdEvents.AD_PROGRESS, onAdProgress);
      manager.addEventListener(imaAdEvents.COMPLETE, onAdCompleted);
      manager.addEventListener(imaAdEvents.CONTENT_RESUME_REQUESTED, () => {
        setIsAdsPlaying(false);
        setShowingAdsCTV(false);
      });

      try {
        manager.init(width, height, window.google.ima.ViewMode.NORMAL);
        manager.start();
      } catch (error) {
        console.error(MESSAGES.ERROR_IMA_LOADING, error);
        setShowingAdsCTV(false);
      }
    };

    loadIma();

    return () => {
      try {
        adsManagerRef.current?.destroy();
        adsLoaderRef.current?.contentComplete();
      } catch (err) {
        console.warn(MESSAGES.ERROR_CLEANING, err);
      }

      adDisplayContainerRef.current = null;
      adsLoaderRef.current = null;
      adsManagerRef.current = null;
      isMounted = false;

      setShowingAdsCTV(false);
      setIsLoading(false);
    };
  }, [adTagUrl, isVisible]);

  useEffect(() => {
    window.addEventListener(BACK_EVENT, handleBack);
    return () => window.removeEventListener(BACK_EVENT, handleBack);
  }, []);

  const handleBack = () => {
    pause();
    setAdsInfo(undefined);
    setIsVisible(false);
    setFullScreen(false);
    setShowingAdsCTV(false);
  };

  const togglePlayPause = () => {
    const manager = adsManagerRef.current;
    if (!manager) return;
    isAdsPlaying ? manager.pause() : manager.resume();
  };

  if (!isVisible) return null;

  return (
    <View style={styles.container}>
      <View ref={adContainerRef} id="ad-container">
        <video
          id="ad-ctv-video"
          ref={videoRef}
          style={{ width: '100%', height: '100%' }}
          playsInline
        />
        {adsInfo && (
          <WebDiv className="navigation-container navigation-priority">
            <View style={styles.adsContainer}>
              <View style={styles.adsTopControlsContainer}>
                <Text style={styles.adsTagText}>Anúncio</Text>
                <Text style={styles.adsTime}>{adsInfo?.adsRemainingTime ?? '00:00'}</Text>
              </View>

              <View style={{ flexDirection: 'row', alignItems: 'center', opacity: 0 }}>
                <Pressable
                  accessibilityRole="button"
                  onPress={togglePlayPause}
                  style={styles.playPauseButton}>
                  <TogglePlayPauseIcon
                    width={calcSizeForTv(40).toString()}
                    height={calcSizeForTv(40).toString()}
                  />
                </Pressable>
              </View>
            </View>
          </WebDiv>
        )}
      </View>
      {isLoading && (
        <WebDiv className="navigation-container navigation-priority" style={styles.adsContainer}>
          <View style={styles.isLoadingContainer}>
            <ActivityIndicator size="large" color="#ffffff" />
          </View>
        </WebDiv>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
  },
  adsContainer: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  adsTopControlsContainer: {
    flexDirection: 'row',
    position: 'relative',
    alignItems: 'center',
    margin: calcSizeForTv(32),
  },
  backButton: {
    marginRight: calcSizeForTv(40),
    padding: calcSizeForTv(4),
  },
  adsTagText: {
    backgroundColor: 'yellow',
    paddingHorizontal: calcSizeForTv(16),
    paddingVertical: calcSizeForTv(12),
    borderRadius: 8,
    color: 'black',
    fontSize: calcSizeForTv(24),
    fontWeight: '600',
  },
  adsTime: {
    backgroundColor: 'rgba(28, 28, 28, 0.66)',
    paddingHorizontal: calcSizeForTv(16),
    paddingVertical: calcSizeForTv(12),
    marginLeft: calcSizeForTv(8),
    borderRadius: 8,
    color: '#FFF',
    fontSize: calcSizeForTv(24),
    fontWeight: '600',
  },
  bottomControlsContainer: {
    flexDirection: 'column',
    justifyContent: 'flex-end',
    position: 'relative',
    marginTop: 'auto',
    height: calcSizeForTv(150),
    padding: calcSizeForTv(32),
  },
  playPauseButton: {
    padding: calcSizeForTv(8),
  },
  isLoadingContainer: {
    position: 'absolute',
    backgroundColor: '#000',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default CTVPlayerWithAds;
